import {
  CardMedia,
  FormControl,
  FormHelperText,
  InputLabel,
  TextField,
  Theme,
} from '@mui/material';
/* Imported PickersDay from the lab layout engine layer */
import { LocalizationProvider, DesktopDatePicker, PickersDay } from '@mui/lab';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import { SxProps } from '@mui/system';
import { useField } from 'formik';
import { useState } from 'react';
/* Imported date evaluator to track current days safely */
import isSameDay from 'date-fns/isSameDay';

const defaultTextFieldStyles: SxProps<Theme> = {
  'label + &': {
    mt: 4,
  },
  '& .MuiInputBase-root': {
    border: '1px solid #DDEAF3',
  },
  '& .MuiOutlinedInput-root': {
    position: 'relative',
    backgroundColor: 'common.white',
    border: '1px solid #DDEAF3',
    fontSize: 16,
    '&:hover': {
      borderColor: 'primary.main',
    },

    '.MuiOutlinedInput-notchedOutline': {
      border: 0,
    },
    '.MuiInputBase-input': {
      p: '10px 12px',
    },

    '&.Mui-focusVisible': {
      outline: '2px solid #2057A6',
      outlineOffset: '2px',
      borderRadius: '4px',
    },

    '&:focus-visible': {
      outline: 'none',
    },
  },
};

export const TextDatePicker = ({
  label = '',
  placeholder = '',
  name,
  inputLabelStyles,
  textFieldStyles = defaultTextFieldStyles,
  maxDate = new Date(),
  minDate,
  disabled = false,
  disableHighlightToday = false,
  onClick,
}: {
  label?: string;
  placeholder: string;
  value?: string | null;
  onClick?: () => void;
  name: string;
  inputLabelStyles?: SxProps<Theme>;
  textFieldStyles?: SxProps<Theme>;
  maxDate?: Date;
  minDate?: Date;
  disabled?: boolean;
  disableHighlightToday?: boolean;
}): JSX.Element => {
  const [field, meta, { setValue }] = useField(name);
  const [error, setError] = useState(false);
  const onChange = (date: any) => {
    date === 'Invalid Date' && setError(true);
    date !== 'Invalid Date' && setError(false);
    setValue(date);
    onClick && onClick();
  };
  const errorText = error ? 'Invalid Date' : meta.error && meta.touched ? meta.error : '';

  const errorId = `${name}-error`;

  /* FIX: Detects if the component text label contains an asterisk symbol */
  const containsAsterisk = label ? label.includes('*') : false;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <FormControl variant="outlined" fullWidth sx={{ my: { xs: 1, md: 2 } }}>
        <InputLabel shrink htmlFor="date-picker" sx={{ ...inputLabelStyles }}>
          {label}
        </InputLabel>
        <DesktopDatePicker
          maxDate={maxDate}
          minDate={minDate}
          components={{
            OpenPickerIcon: () => (
              <CardMedia
                component="img"
                src={process.env.PUBLIC_URL + '/images/calender.svg'}
                alt="Calender Image"
              />
            ),
          }}
          componentsProps={{}}
          value={field.value}
          inputFormat="dd/MM/yyyy"
          onChange={onChange}
          disabled={disabled}
          views={['year', 'month', 'day']}
          InputAdornmentProps={{ position: 'start' }}
          renderDay={(day, selectedDays, pickersDayProps) => {
            const isToday = isSameDay(day, new Date());
            const dayNumber = day.getDate();

            return (
              <PickersDay
                {...pickersDayProps}
                {...(isToday && {
                  'aria-current': 'date',
                  'aria-label': `Today, ${dayNumber}`,
                })}
              />
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              fullWidth
              id="date-picker"
              placeholder={placeholder}
              aria-invalid={!!errorText}
              inputProps={{
                ...params.inputProps,
                /* FIX: Programmatically ties the input to the error helper log text block */
                ...(errorText && { 'aria-describedby': errorId }),
                /* FIX: Appends screen reader voice override labels dynamically when asterisks are found */
                ...(containsAsterisk && {
                  'aria-label': `${label.replace('*', '').trim()} required`,
                  'aria-required': true,
                }),
              }}
              sx={{
                ...textFieldStyles,
                display: 'flex',
                flexDirection: 'row-reverse',
                '& .Mui-disabled': {
                  cursor: !disabled ? 'text' : 'not-allowed !important',
                  pointerEvents: 'all',
                },
              }}
              disabled={disabled}
            />
          )}
          disableCloseOnSelect={false}
          showToolbar={false}
          disableHighlightToday={disableHighlightToday}
        />

        {errorText && (
          <FormHelperText
            /* FIX: Re-mapped semantic error context to trigger validation state colors */
            error
            id={errorId}
            role="alert"
            aria-live="assertive"
            sx={{ marginLeft: 'unset' }}>
            {errorText}
          </FormHelperText>
        )}
      </FormControl>
    </LocalizationProvider>
  );
};
